import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import AdminSidebar from '../AdminSidebar';

// ========================================
// Global Styles - Reset body margin for admin pages
// ========================================

const AdminGlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
  }
`;

// ========================================
// Styled Components - Matching Legacy FE
// ========================================

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const TopNavbar = styled.nav`
  background: #2d2d2d;
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HamburgerButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 36px;
  width: 40px;
  box-sizing: border-box;
  
  &:hover span {
    background: #fff;
  }
`;

const HamburgerLine = styled.span`
  display: block;
  height: 2px;
  width: 100%;
  background: #b8b8b8;
  transition: background 0.2s ease;
`;

const NavBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
  font-size: 14px;
  color: #fff;
`;

const NavLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const NavSeparator = styled.span`
  margin: 0 8px;
  color: #b8b8b8;
`;

const NavText = styled.span`
  color: #fff;
`;

const PageTitle = styled.span`
  color: #fff;
  font-size: 14px;
  margin-left: 16px;
`;

const MainContent = styled.main`
  flex: 1;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

// ========================================
// Component
// ========================================

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  pageTitle?: string;
  breadcrumb?: BreadcrumbItem[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, pageTitle, breadcrumb }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Set document title to match legacy FE format
  // Legacy builds title from breadcrumbs: reversed labels joined with " : ", then " : Admin tool : Contently"
  // e.g., breadcrumbs [Publications, Account - Pub, Users] => "Users : Account - Pub : Publications : Admin tool : Contently"
  useEffect(() => {
    let pageTitlePart = title;
    
    if (breadcrumb && breadcrumb.length > 0) {
      // Collect all breadcrumb labels, reverse them, and join with " : "
      pageTitlePart = breadcrumb.map(item => item.label).reverse().join(' : ');
    }
    
    document.title = `${pageTitlePart} : Admin tool : Contently`;
  }, [title, breadcrumb]);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const renderNavContent = () => {
    if (breadcrumb && breadcrumb.length > 0) {
      return (
        <NavBreadcrumb>
          {breadcrumb.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <NavSeparator>›</NavSeparator>}
              {item.href ? (
                <NavLink to={item.href}>{item.label}</NavLink>
              ) : (
                <NavText>{item.label}</NavText>
              )}
            </React.Fragment>
          ))}
        </NavBreadcrumb>
      );
    }
    return <PageTitle>{pageTitle || title}</PageTitle>;
  };

  return (
    <LayoutContainer>
      <AdminGlobalStyle />
      <AdminSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      <TopNavbar>
        <HamburgerButton onClick={toggleSidebar} aria-label="Toggle menu">
          <HamburgerLine />
          <HamburgerLine />
          <HamburgerLine />
        </HamburgerButton>
        {renderNavContent()}
      </TopNavbar>
      
      <MainContent>
        <ContentWrapper>
          {children}
        </ContentWrapper>
      </MainContent>
    </LayoutContainer>
  );
};

export default AdminLayout;
